import React from 'react';
import { Receipt, FileSignature, Mail, BookOpen, FileText, Archive } from 'lucide-react';

const TYPES = [
  {
    icon: FileSignature,
    title: 'Contracts and engagement letters',
    body:
      'Engagement letters, fee agreements, NDAs, releases, and amendments. Sortio reads each contract for the party names and effective date, then routes it under the right client matter. Renamed templates look like 2026-04-12 - Engagement Letter - Smith Matter 00012.pdf so a quick Finder search finds them instantly.',
  },
  {
    icon: Receipt,
    title: 'Invoices and trust accounting docs',
    body:
      'Outside vendor bills, expert witness invoices, court filing fees, trust deposit receipts, and IOLTA reconciliations. Sortio separates billing artifacts from substantive case docs, which keeps your bookkeeper happy and your billing audit defensible. Pair this with the receipt naming pattern in our PDF rename guide.',
  },
  {
    icon: Mail,
    title: 'Correspondence',
    body:
      'Letters, emails saved as PDF, faxes, certified mail receipts. The "correspondence" subfolder under each matter fills up fast; Sortio identifies the sender, recipient, and date, names the file accordingly, and stamps a tag so you can filter to "client-facing" vs "opposing counsel" vs "court."',
  },
  {
    icon: BookOpen,
    title: 'Pleadings and discovery',
    body:
      'Complaints, answers, motions, briefs, discovery requests and responses, deposition transcripts, expert reports. Sortio tags court filings by docket number, hearing date, and document type. Combined with the Rule Builder you can split discovery responses by request set and party.',
  },
  {
    icon: FileText,
    title: 'Client-supplied evidence',
    body:
      'Tax returns, bank statements, photos, medical records, contracts, screenshots. The messiest pile. Sortio reads the content and tags by source (client vs third party), date range, and document class. Photos of damage or scene are organized by capture date and grouped per matter.',
  },
  {
    icon: Archive,
    title: 'Closed matter archives',
    body:
      'When a matter closes, the Rule Builder packs the whole tree into a retention archive labeled by closing date and state retention class. Files become read-only, and the audit log captures everything in one place. When the retention window expires, the same rule flags the archive for deletion review.',
  },
];

const DocumentTypes = () => {
  return (
    <section id="document-types" className="my-20">
      <div className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Filing patterns for the documents you actually handle
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Sortio is not generic. It recognizes the document categories that show up inside a legal
          practice and routes them accordingly.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {TYPES.map((type, i) => {
          const Icon = type.icon;
          return (
            <div
              key={i}
              className="bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 p-6"
            >
              <div className="w-10 h-10 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 flex items-center justify-center mb-4">
                <Icon className="text-sortio-primary" size={18} />
              </div>
              <h3 className="text-lg font-medium text-apple-50 mb-3">{type.title}</h3>
              <p className="text-sm text-apple-200 leading-relaxed">{type.body}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default DocumentTypes;
